/**
 * The single frontend home for per-state facts. Every US state plus DC is listed;
 * `launched` is what separates the ones we sell in from the ones the public waitlist
 * form still has to offer.
 *
 * Launching a state is flipping `launched` here plus one `mapping` row in
 * pkg/utils/timezone.go (the backend drift guard — a state launched here but missing
 * there emails on the store's first sync).
 *
 * The launched rows come first and in launch order: LAUNCHED_STATE_CODES_TEXT renders
 * them in that order into marketing copy ("AR, OK, NY & CO").
 */
type StateInfo = {
  code: string;
  name: string;
  launched: boolean;
  swingDeals: boolean;
};

const STATES: StateInfo[] = [
  { code: 'AR', name: 'Arkansas', launched: true, swingDeals: false },
  { code: 'OK', name: 'Oklahoma', launched: true, swingDeals: true },
  { code: 'NY', name: 'New York', launched: true, swingDeals: false },
  { code: 'CO', name: 'Colorado', launched: true, swingDeals: false },

  // Not launched yet. Present so the public waitlist form can ask any US prospect
  // where they are, and so launching is a flag flip rather than a new row.
  { code: 'AL', name: 'Alabama', launched: false, swingDeals: false },
  { code: 'AK', name: 'Alaska', launched: false, swingDeals: false },
  { code: 'AZ', name: 'Arizona', launched: false, swingDeals: false },
  { code: 'CA', name: 'California', launched: false, swingDeals: false },
  { code: 'CT', name: 'Connecticut', launched: false, swingDeals: false },
  { code: 'DE', name: 'Delaware', launched: false, swingDeals: false },
  { code: 'DC', name: 'District of Columbia', launched: false, swingDeals: false },
  { code: 'FL', name: 'Florida', launched: false, swingDeals: false },
  { code: 'GA', name: 'Georgia', launched: false, swingDeals: false },
  { code: 'HI', name: 'Hawaii', launched: false, swingDeals: false },
  { code: 'ID', name: 'Idaho', launched: false, swingDeals: false },
  { code: 'IL', name: 'Illinois', launched: false, swingDeals: false },
  { code: 'IN', name: 'Indiana', launched: false, swingDeals: false },
  { code: 'IA', name: 'Iowa', launched: false, swingDeals: false },
  { code: 'KS', name: 'Kansas', launched: false, swingDeals: false },
  { code: 'KY', name: 'Kentucky', launched: false, swingDeals: false },
  { code: 'LA', name: 'Louisiana', launched: false, swingDeals: false },
  { code: 'ME', name: 'Maine', launched: false, swingDeals: false },
  { code: 'MD', name: 'Maryland', launched: false, swingDeals: false },
  { code: 'MA', name: 'Massachusetts', launched: false, swingDeals: false },
  { code: 'MI', name: 'Michigan', launched: false, swingDeals: false },
  { code: 'MN', name: 'Minnesota', launched: false, swingDeals: false },
  { code: 'MS', name: 'Mississippi', launched: false, swingDeals: false },
  { code: 'MO', name: 'Missouri', launched: false, swingDeals: false },
  { code: 'MT', name: 'Montana', launched: false, swingDeals: false },
  { code: 'NE', name: 'Nebraska', launched: false, swingDeals: false },
  { code: 'NV', name: 'Nevada', launched: false, swingDeals: false },
  { code: 'NH', name: 'New Hampshire', launched: false, swingDeals: false },
  { code: 'NJ', name: 'New Jersey', launched: false, swingDeals: false },
  { code: 'NM', name: 'New Mexico', launched: false, swingDeals: false },
  { code: 'NC', name: 'North Carolina', launched: false, swingDeals: false },
  { code: 'ND', name: 'North Dakota', launched: false, swingDeals: false },
  { code: 'OH', name: 'Ohio', launched: false, swingDeals: false },
  { code: 'OR', name: 'Oregon', launched: false, swingDeals: false },
  { code: 'PA', name: 'Pennsylvania', launched: false, swingDeals: false },
  { code: 'RI', name: 'Rhode Island', launched: false, swingDeals: false },
  { code: 'SC', name: 'South Carolina', launched: false, swingDeals: false },
  { code: 'SD', name: 'South Dakota', launched: false, swingDeals: false },
  { code: 'TN', name: 'Tennessee', launched: false, swingDeals: false },
  { code: 'TX', name: 'Texas', launched: false, swingDeals: false },
  { code: 'UT', name: 'Utah', launched: false, swingDeals: false },
  { code: 'VT', name: 'Vermont', launched: false, swingDeals: false },
  { code: 'VA', name: 'Virginia', launched: false, swingDeals: false },
  { code: 'WA', name: 'Washington', launched: false, swingDeals: false },
  { code: 'WV', name: 'West Virginia', launched: false, swingDeals: false },
  { code: 'WI', name: 'Wisconsin', launched: false, swingDeals: false },
  { code: 'WY', name: 'Wyoming', launched: false, swingDeals: false },
];

export const LAUNCHED_STATES = STATES.filter((s) => s.launched).map((s) => s.code);
export const LAUNCHED_STATE_NAMES = STATES.filter((s) => s.launched).map((s) => s.name);

/** Every state plus DC, by name, for the public waitlist form's state picker. */
export const US_STATE_OPTIONS = [...STATES]
  .sort((a, b) => a.name.localeCompare(b.name))
  .map((s) => ({ value: s.code, label: s.name }));

function joinWithFinal(items: string[], final: string): string {
  if (items.length <= 1) {
    return items[0] ?? '';
  }

  return `${items.slice(0, -1).join(', ')} ${final} ${items[items.length - 1]}`;
}

/** "AR, OK, NY & CO" — for compact marketing copy. */
export const LAUNCHED_STATE_CODES_TEXT = joinWithFinal(LAUNCHED_STATES, '&');

/** "Arkansas, Oklahoma, New York and Colorado" — for prose marketing copy. */
export const LAUNCHED_STATE_NAMES_TEXT = joinWithFinal(LAUNCHED_STATE_NAMES, 'and');

/**
 * Swing deals are an Oklahoma market convention (post-and-hold pricing).
 * Frontend-only fact: it drives the four swing/buyup order columns and has
 * zero backend footprint.
 */
export function hasSwingDeals(store: { billing_address: { state?: string | null } }): boolean {
  return STATES.find((s) => s.code === store.billing_address.state)?.swingDeals ?? false;
}
